import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from './AuthContext';

/**
 * Gates a route behind a valid session, and optionally behind having at least one
 * active account.
 *
 * <p>Waits for the initial session probe before deciding, so a signed-in user
 * reloading a deep link is not bounced to sign-in and back.
 *
 * <p>{@code requireActiveAccount} is the direct-URL-bypass guard for
 * {@code user.needsRoleSelection}: {@code Options.tsx} already hides Program
 * Management/Curling Codex behind the Get Started flow for that state, but that alone
 * doesn't stop someone from typing the URL in directly — this does, by sending them
 * back to {@code /options}, where the same flow lives.
 */
export function RequireAuth(
  { children, requireActiveAccount = false }: { children?: ReactNode; requireActiveAccount?: boolean }
) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 flex items-center justify-center">
        <p className="text-blue-300">Loading…</p>
      </div>
    );
  }

  if (!user) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  if (requireActiveAccount && user.needsRoleSelection) {
    return <Navigate to="/options" replace />;
  }

  return <>{children}</>;
}
